import type {
  RotationCalendarAttributes,
  RotationCalendarRotation,
  RotationCalendarSemester,
  RotationType,
} from "@shared/models/rotation-calendar"
import { isNil } from "lodash-es"
import { generateDates } from "./dates"

type Omit<T, K extends keyof T> = Pick<T, Exclude<keyof T, K>>

export interface RCAttributes
  extends Omit<RotationCalendarAttributes, "_revision" | "_revisedBySessionId" | "version"> {}

export const WEEKDAY_ROTATIONS = [
  { id: "sunday", title: "Sunday", abbreviation: "Su", position: 500 },
  { id: "monday", title: "Monday", abbreviation: "M", position: 1000 },
  { id: "tuesday", title: "Tuesday", abbreviation: "Tu", position: 2000 },
  { id: "wednesday", title: "Wednesday", abbreviation: "W", position: 3000 },
  { id: "thursday", title: "Thursday", abbreviation: "Th", position: 4000 },
  { id: "friday", title: "Friday", abbreviation: "F", position: 5000 },
  { id: "saturday", title: "Saturday", abbreviation: "Sa", position: 6000 },
]

export function createRotationCalendarAttributes(opts: {
  rotationType: RotationType
  semester: RotationCalendarSemester
  schoolYearId: string
  schoolDays: Array<number>
  parentRotationCalendarId?: string
  rotations?: Array<RotationCalendarRotation>
  datesLastChangedOn?: string
}): RCAttributes {
  // Set the rotations
  let rotations: Array<RotationCalendarRotation>
  if (opts.rotationType === "weekly") {
    rotations = WEEKDAY_ROTATIONS.filter((_rotation, index) => opts.schoolDays.includes(index))
  } else if (opts.rotationType === "block") {
    if (isNil(opts.rotations)) {
      throw new Error("Block rotations must also pass in rotations")
    }
    rotations = opts.rotations
  } else {
    throw new Error("Bi-weekly rotationType not yet supported")
  }

  // Create the dates
  let dates = generateDates({
    semesters: [opts.semester],
    dateAnnotations: [],
    schoolDays: opts.schoolDays,
    rotationType: opts.rotationType,
    rotations: rotations,
  })

  return {
    rotationType: opts.rotationType,
    schoolDays: opts.schoolDays,
    rotations: rotations,
    dateAnnotations: [],
    dates: dates,
    schoolYearId: opts.schoolYearId,
    datesLastChangedOn: opts.datesLastChangedOn || null,
    datesLastUpdatedAtVersion: null,
    datesUpdatedToParentVersion: null,
    parentRotationCalendarId: opts.parentRotationCalendarId || null,
    semesters: [opts.semester],
  }
}
